import { Maybe } from '@ardoq/common-helpers';
import { type I18nLocale, t } from '@ardoq/i18n';

const START_DATE_FIELD_NAME_SUFFIX = '_start_date';
const END_DATE_FIELD_NAME_SUFFIX = '_end_date';
const FIELD_NAME_PATTERN_START_DATE = /^(.*)_start_date$/i;
const FIELD_NAME_PATTERN_END_DATE = /^(.[^,]*)_end_date$/i;

export const fieldNameIsDateRangeStartField = (fieldName: string) =>
  FIELD_NAME_PATTERN_START_DATE.test(fieldName);
export const fieldNameIsDateRangeEndField = (fieldName: string) =>
  FIELD_NAME_PATTERN_END_DATE.test(fieldName);
export const fieldNameIsDateRangeStartOrEndField = (fieldName: string) =>
  fieldNameIsDateRangeStartField(fieldName) ||
  fieldNameIsDateRangeEndField(fieldName);
export const isCombinedDateRangeFieldNames = (fieldName: string) => {
  const [start, end] = fieldName.split(',');
  return Boolean(
    start &&
    end &&
    fieldNameIsDateRangeStartField(start) &&
    fieldNameIsDateRangeEndField(end)
  );
};
export const fieldNameIsPartOfDateRangeField = (fieldName: string) =>
  fieldNameIsDateRangeStartField(fieldName) ||
  fieldNameIsDateRangeEndField(fieldName) ||
  isCombinedDateRangeFieldNames(fieldName);

export const toEndDateName = (fieldName: string) =>
  `${fieldName}${END_DATE_FIELD_NAME_SUFFIX}`;

export const toStartDateName = (fieldName: string) =>
  `${fieldName}${START_DATE_FIELD_NAME_SUFFIX}`;

export const getStartDateName = (fieldName: string) => fieldName.split(',')[0];

export const getEndDateName = (fieldName: string) => fieldName.split(',')[1];

export const createCombinedDateRangeFieldNames = (fieldName: string) =>
  `${toStartDateName(fieldName)},${toEndDateName(fieldName)}`;

export const extractDateRangeFieldName = (fieldName: string): string => {
  if (isCombinedDateRangeFieldNames(fieldName)) {
    const [start] = fieldName.split(',');
    return extractDateRangeFieldName(start);
  }
  const dateRangeFieldName = (fieldName.match(FIELD_NAME_PATTERN_START_DATE) ||
    fieldName.match(FIELD_NAME_PATTERN_END_DATE))?.[1];
  if (dateRangeFieldName) return dateRangeFieldName;
  throw new Error('Could not extract date range field name');
};

/**
 * Takes a date time field name, which is supposed to have START_DATE_FIELD_NAME_SUFFIX or
 * END_DATE_FIELD_NAME_SUFFIX, and returns the other field name that is part of the same
 * date range
 */
export const getDateRangeDateTimeFieldCounterpartName = (
  dateRangeFieldName: string
) => {
  const commonDateRangeFieldName =
    extractDateRangeFieldName(dateRangeFieldName);

  const isCurrentFieldStartField =
    fieldNameIsDateRangeStartField(dateRangeFieldName);

  return isCurrentFieldStartField
    ? toEndDateName(commonDateRangeFieldName)
    : toStartDateName(commonDateRangeFieldName);
};

/**
 * This function does not care about the field value, it answers the question "What
 * string should I display if my date field is empty", whatever "empty" means.
 *
 * This should be based on the field name, and we don't check if the field has a valid
 * counterpart in the scope data. If the field is named with "start_date", it is a
 * start date
 */
export const getDefinedEmptyDateRangeFieldValueString = (
  fieldName: string,
  i18nLocale: I18nLocale
) => {
  if (fieldNameIsDateRangeStartField(fieldName)) {
    return t(i18nLocale, 'dateRange:startDate.empty', 'No start date');
  }
  if (fieldNameIsDateRangeEndField(fieldName)) {
    return t(i18nLocale, 'dateRange:endDate.empty', 'No end date');
  }
  return null;
};

export const getDateRangeFieldNamePairsFromFieldNames = (
  fieldNames: string[]
): Record<string, { start: string; end: string }> =>
  Object.entries(
    fieldNames.reduce<Record<string, { start?: string; end?: string }>>(
      // find all potential pairs
      (potentialPairs, fieldName) => {
        if (fieldNameIsPartOfDateRangeField(fieldName)) {
          const dateRangeFieldName = extractDateRangeFieldName(fieldName);
          const fieldsObject = fieldNameIsDateRangeStartField(fieldName)
            ? { start: fieldName }
            : { end: fieldName };

          return {
            ...potentialPairs,
            [dateRangeFieldName]: potentialPairs[dateRangeFieldName]
              ? {
                  ...potentialPairs[dateRangeFieldName],
                  ...fieldsObject,
                }
              : fieldsObject,
          };
        }
        return potentialPairs;
      },
      {}
    )
  ).reduce<Record<string, { start: string; end: string }>>(
    // return only pairs with both start and end
    (pairs, [fieldName, { start, end }]) =>
      start && end ? { ...pairs, [fieldName]: { start, end } } : pairs,
    {}
  );

export const maybeExpandDateRangeFieldName = (
  fieldName: string
): Maybe<[string, string]> => {
  if (isCombinedDateRangeFieldNames(fieldName)) {
    return fieldName.split(',') as [string, string];
  }
  return null;
};

export const maybeGetCombinedDateRangeFieldName = (
  fieldName: string
): Maybe<string> => {
  if (
    fieldNameIsDateRangeStartField(fieldName) ||
    fieldNameIsDateRangeEndField(fieldName)
  ) {
    const baseFieldName = extractDateRangeFieldName(fieldName);
    return createCombinedDateRangeFieldNames(baseFieldName);
  }
  return null;
};

export const normalizeDateRangeFieldNames = (fieldNames: string[]) =>
  fieldNames.reduce<string[]>((acc, fieldName) => {
    if (isCombinedDateRangeFieldNames(fieldName)) {
      return [...acc, fieldName];
    }
    if (fieldNameIsDateRangeEndField(fieldName)) return acc;
    const normalizedFieldName =
      maybeGetCombinedDateRangeFieldName(fieldName) ?? fieldName;
    return [...acc, normalizedFieldName];
  }, []);
